import { Fingerprint } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { ToneName } from '@/theme';
import type { DropiHuella } from '@/types/dropi';

/**
 * Huella digital del cliente según Dropi (docs/DROPI.md).
 *
 * Dropi devuelve una etiqueta de riesgo y un color; acá se traducen al tono del sistema de diseño.
 * El texto resume lo que de verdad se mira al confirmar un pedido contraentrega: cuántas órdenes
 * ha recibido y cuántas ha devuelto (una devolución cambia el semáforo aunque el color venga verde).
 */
export function DropiHuellaBadge({ huella }: { huella: DropiHuella | null | undefined }) {
  if (!huella) return null;

  if (!huella.encontrado) {
    return (
      <Badge tone="neutral" icon={<Fingerprint size={12} aria-hidden="true" />}>
        Sin historial en Dropi
      </Badge>
    );
  }

  const tone = tonoDeRiesgo(huella);
  const devoluciones = huella.devueltas > 0 ? ` · ${huella.devueltas} devueltas` : '';

  return (
    <Badge tone={tone} dot icon={<Fingerprint size={12} aria-hidden="true" />}>
      {huella.riskLabel ?? 'Historial'} · {huella.ordenes}{' '}
      {huella.ordenes === 1 ? 'orden' : 'órdenes'}
      {devoluciones}
    </Badge>
  );
}

/** `green`/`yellow`/`red` de Dropi (o el conteo de devoluciones) → tono del panel. */
function tonoDeRiesgo(huella: DropiHuella): ToneName {
  if (huella.riskColor === 'green') return 'success';
  if (huella.riskColor === 'yellow') return 'warning';
  if (huella.riskColor === 'red') return 'danger';

  // Sin color del backend, decide el dato duro: devolvió algo → ojo.
  return huella.devueltas > 0 ? 'warning' : 'neutral';
}

/**
 * Texto largo del informe (para el detalle de la orden o el título del badge).
 * Ej.: «Segura (Esporádico) · 2 órdenes, 2 entregadas, 0 devueltas».
 */
export function describeHuella(huella: DropiHuella): string {
  if (!huella.encontrado) return 'Dropi no tiene historial para este teléfono.';

  const partes = [`${huella.ordenes} órdenes`, `${huella.entregadas} entregadas`, `${huella.devueltas} devueltas`];
  const perfil = huella.buyerType ? ` (${huella.buyerType})` : '';
  return `${huella.riskLabel ?? 'Sin etiqueta'}${perfil} · ${partes.join(', ')}`;
}
